/**
 * pageSnapshot.js
 *
 * Flattens the full nested page builder state into a lightweight, flat array
 * of components that can be sent to the AI without exceeding context limits.
 *
 * Each entry contains just enough info for the AI to:
 *  - Identify the component (id, type)
 *  - Understand its content (key props)
 *  - Know where it lives (rowId, sectionId, columnIdx) for targeting actions
 */

/**
 * Extract only the props the AI needs to understand a component's content.
 * We deliberately exclude heavy style objects to keep the snapshot small.
 * BUT we do include a summary of existing styles so the AI knows what's set.
 */
function extractKeyProps(type, props) {
  if (!props || typeof props !== 'object') return {};

  const keysByType = {
    heading:           ['text', 'level', 'fontSize', 'color', 'fontWeight', 'textAlign'],
    text:              ['content', 'fontSize', 'color', 'textAlign'],
    button:            ['text', 'variant', 'backgroundColor', 'color', 'href', 'size'],
    image:             ['imgSrc', 'alt', 'caption', 'width', 'height'],
    brandLogo:         ['imgSrc', 'alt'],
    video:             ['src', 'autoplay', 'controls'],
    audio:             ['src'],
    iframe:            ['src', 'height', 'width'],
    divider:           ['color', 'thickness', 'style'],
    spacer:            ['height'],
    badge:             ['text', 'variant'],
    blockquote:        ['quote', 'author'],
    list:              ['items'],
    alert:             ['type', 'message'],
    richText:          ['content'],
    map:               ['location', 'zoom'],
    contactForm:       ['formFields'],
    breadcrumb:        ['items', 'separator', 'template'],
    socialMediaIcons:  ['links'],
    countdownTimer:    ['targetDate'],
    progress:          ['items'],
    timeline:          ['events'],
    tabs:              ['tabs'],
    accordion:         ['items'],
    carousel:          ['items'],
    modal:             ['title', 'body', 'buttonLabel'],
    gallery:           ['images'],
    lightboxGallery:   ['images'],
    pricingTable:      ['plans'],
    faq:               ['title', 'faqs'],
    featureCards:      ['headline', 'description', 'features'],
    teamSection:       ['headline', 'description', 'teamMembers'],
    testimonialComponent: ['headline', 'description', 'testimonials'],
    cardComponent:     ['title', 'imageUrl', 'bodyText'],
    imageOverlayCard:  ['title', 'imageUrl', 'bodyText'],
    headerFooterCard:  ['header', 'footer', 'title', 'bodyText'],
    horizontalCard:    ['title', 'imageUrl', 'bodyText'],
    listGroupCard:     ['title', 'items'],
    iconCard:          ['icon', 'title', 'description'],
  };

  const keys = keysByType[type];
  const result = {};

  if (keys) {
    keys.forEach((k) => {
      if (props[k] !== undefined && props[k] !== null) {
        result[k] = props[k];
      }
    });
  } else {
    // Fallback: take first 6 keys for unknown types
    Object.entries(props)
      .filter(([k, v]) => k !== 'styles' && v !== null && v !== undefined && typeof v !== 'function')
      .slice(0, 6)
      .forEach(([k, v]) => { result[k] = v; });
  }

  // Include a compact summary of existing desktop styles so AI knows what's already set
  if (props.styles?.desktop) {
    const desktopStyles = props.styles.desktop;
    const stylesSummary = {};
    Object.entries(desktopStyles).forEach(([section, sectionStyles]) => {
      if (sectionStyles && typeof sectionStyles === 'object' && Object.keys(sectionStyles).length > 0) {
        stylesSummary[section] = sectionStyles;
      }
    });
    if (Object.keys(stylesSummary).length > 0) {
      result._currentStyles = stylesSummary;
    }
  }

  return result;
}

/**
 * Flatten a single column's components into snapshot entries.
 */
function flattenColumnComponents(
  components,
  rowId,
  sectionId,
  columnIdx,
  isSubColumn = false,
  subSectionId = null,
  rowNumber = null,
  rowLabel = null,
  columnNumber = null
) {
  if (!Array.isArray(components)) return [];

  return components.map((comp, compIdx) => ({
    componentId: comp.id || `${sectionId}_col${columnIdx}_comp${compIdx}`,
    componentIdx: compIdx,
    type: comp.type,
    rowId,
    rowNumber,
    rowLabel,       // e.g. "Row 2 (carousel)" — for human reference
    columnNumber,   // 1-based column number within the section
    sectionId,
    columnIdx,
    ...(isSubColumn && subSectionId ? { subSectionId, isSubComponent: true } : {}),
    props: extractKeyProps(comp.type, comp.props),
    disabled: comp.disabled || false,
  }));
}

/**
 * Main export: flatten the full rows state into a compact snapshot array.
 *
 * @param {Array} rows - The rows array from the pageBuilderReducer state
 * @returns {Array} - Flat array of component descriptors for the AI
 */
export function flattenPageForAI(rows) {
  if (!Array.isArray(rows)) return [];

  const snapshot = [];

  rows.forEach((row, rowIndex) => {
    if (!row || row.disabled) return;

    // Human-readable row label — "Row 1", "Row 2" etc.
    // Also derive a content label from the first component found in the row
    const rowNumber = rowIndex + 1;
    const firstComponentType = getFirstComponentType(row);
    const rowLabel = firstComponentType
      ? `Row ${rowNumber} (${firstComponentType})`
      : `Row ${rowNumber}`;

    // Add row-level entry so AI knows about rows for style changes
    snapshot.push({
      entityType: 'row',
      rowId: row.id,
      rowNumber,
      rowLabel,   // human-readable: "Row 1 (heading)", "Row 2 (carousel)" etc.
      styles: row.styles || {},
    });

    (row.sections || []).forEach((section, sectionIndex) => {
      if (!section || section.disabled) return;

      // Add section-level entry — AI needs sectionId + columnCount to target ADD_COMPONENT_TO_EXISTING
      snapshot.push({
        entityType: 'section',
        rowId: row.id,
        rowNumber,
        rowLabel,
        sectionId: section.id,
        sectionNumber: sectionIndex + 1,
        columnCount: (section.columns || []).length,
        columnClasses: (section.columns || []).map((c) => c.class || 'col-lg-12'),
        styles: section.styles || {},
      });

      (section.columns || []).forEach((col, colIdx) => {
        if (!col) return;

        const topComponents = flattenColumnComponents(
          col.components || [],
          row.id,
          section.id,
          colIdx,
          false,
          null,
          rowNumber,
          rowLabel,
          colIdx + 1
        );
        snapshot.push(...topComponents);

        (col.sections || []).forEach((subSection) => {
          if (!subSection || subSection.disabled) return;

          snapshot.push({
            entityType: 'subSection',
            rowId: row.id,
            rowNumber,
            rowLabel,
            sectionId: section.id,
            subSectionId: subSection.id,
            columnIdx: colIdx,
            styles: subSection.styles || {},
          });

          (subSection.columns || []).forEach((subCol, subColIdx) => {
            if (!subCol) return;
            const subComponents = flattenColumnComponents(
              subCol.components || [],
              row.id,
              subSection.id,
              subColIdx,
              true,
              subSection.id,
              rowNumber,
              rowLabel,
              subColIdx + 1
            );
            snapshot.push(...subComponents);
          });
        });
      });
    });
  });

  return snapshot;
}

/**
 * Get the type of the first non-structural component in a row (for labelling).
 */
function getFirstComponentType(row) {
  for (const section of row.sections || []) {
    for (const col of section.columns || []) {
      if (col.components && col.components.length > 0) {
        return col.components[0].type;
      }
    }
  }
  return null;
}

/**
 * Find a component in the rows state by its ID.
 * Returns { component, sectionId, columnIdx, componentIdx } or null.
 */
export function findComponentById(rows, componentId) {
  for (const row of rows) {
    for (const section of row.sections || []) {
      for (let colIdx = 0; colIdx < (section.columns || []).length; colIdx++) {
        const col = section.columns[colIdx];
        for (let compIdx = 0; compIdx < (col.components || []).length; compIdx++) {
          const comp = col.components[compIdx];
          if (comp.id === componentId || `${section.id}_col${colIdx}_comp${compIdx}` === componentId) {
            return {
              component: comp,
              rowId: row.id,
              sectionId: section.id,
              columnIdx: colIdx,
              componentIdx: compIdx,
              isSubComponent: false,
            };
          }
        }
        // Check subsections
        for (const subSection of col.sections || []) {
          for (let subColIdx = 0; subColIdx < (subSection.columns || []).length; subColIdx++) {
            const subCol = subSection.columns[subColIdx];
            for (let compIdx = 0; compIdx < (subCol.components || []).length; compIdx++) {
              const comp = subCol.components[compIdx];
              if (
                comp.id === componentId ||
                `${subSection.id}_col${subColIdx}_comp${compIdx}` === componentId
              ) {
                return {
                  component: comp,
                  rowId: row.id,
                  sectionId: subSection.id,
                  columnIdx: subColIdx,
                  componentIdx: compIdx,
                  isSubComponent: true,
                  parentSectionId: section.id,
                  parentColumnIdx: colIdx,
                  subSectionId: subSection.id,
                };
              }
            }
          }
        }
      }
    }
  }
  return null;
}
